// Resolve the effective per-patient display config for a treatment plan by
// layering, per key:
//   1. the plan's saved per-patient pricingDisplay (highest precedence)
//   2. the practice's "house style" defaults (Settings → Presentation)
//   3. a hardcoded safety net (matches the historical default behavior)
//
// The per-patient layer wins ONLY for keys it explicitly set — so an explicit
// `false` from the TC still beats a practice `true`. Using "key present?"
// (hasOwnProperty) instead of the old `!== false` idiom is what makes the
// three-level precedence correct; `!== false` conflated "unset" with "true"
// and would have masked the practice default entirely.
//
// IMPORTANT: keep this logic in sync with the backend twin at
// teamio-serverless-api/tx-presenter-service/lib/resolveDisplay.js — the patient
// view and signed PDF are rendered server-side and must resolve identically.
// Everything between the SHARED BODY markers is BYTE-IDENTICAL across the two
// files and pinned by the backend's tests/pricing-mode.test.js ("resolveDisplay
// twin parity"), which reads BOTH files and compares their shared bodies. Edit
// both, or edit neither.
//
// (This header used to cite tests/resolve-display-twin-parity.test.js — a file
// that has never existed. Corrected 2026-08-11 while adding showPhotos.)

// ─────────────────────────── SHARED BODY START ───────────────────────────

// Per-view pricing modes (2026-08-05). Replaces the viewVisibility boolean as
// the primary control:
//   'full'  — per-procedure fees (gated by `layers`) + the Plan Summary card
//   'total' — NO per-row fees; only the Plan Summary total at the bottom
//   'none'  — no money anywhere in that view (what viewVisibility:false did)
//
// 'total' is the mode a production client asked for: they expected "Itemized"
// to be a PRICING distinction, with the non-itemized Arch & Region view able to
// show just a plan total. Before this there was no combination of settings that
// produced it — turning all four `layers` off got close but was global, and
// viewVisibility:false hid the summary card too.
const PRICING_MODES = ['full', 'total', 'none'];

const HARDCODED = {
  pricing: { region: 'full', itemized: 'full' },
  viewVisibility: { region: true, itemized: true },
  layers: { netFee: true, standardFee: true, insuranceEstimate: true, membership: true },
  itemizedGroupByPriority: false,
  itemizedGroupLabel: 'Priority',
  itemizedGroupSubtotals: true,
  showDiagnostic: true,
  // Show the guarantor's Open Dental account balance / credit as a Plan Summary
  // line, folded into the Estimated Patient Portion. Default ON for every office.
  showBalance: true,
  // Show the patient's photo library on this plan: the camera icon beside a
  // tooth number in the Itemized/By Region views, and the "Photos of Planned
  // Teeth" gallery. Default ON — an office that has gone to the trouble of
  // attaching a photo means for the patient to see it.
  //
  // Note this is the ONLY gate. Photos are patient-level, so a plan built
  // before any photo existed picks them up the moment one is attached; there is
  // no per-plan opt-in step. Turning this off is how an office keeps a
  // particular plan photo-free.
  showPhotos: true,
};

function has(obj, key) {
  return obj != null && Object.prototype.hasOwnProperty.call(obj, key);
}

// Three-level boolean precedence: per-patient → practice → hardcoded.
function resolveBool(perPatient, practice, hard) {
  if (typeof perPatient === 'boolean') return perPatient;
  if (typeof practice === 'boolean') return practice;
  return hard;
}

function resolveNested(subKey, keys, pd, pdef) {
  const pdSub = (pd && pd[subKey]) || {};
  const defSub = (pdef && pdef[subKey]) || {};
  const hardSub = HARDCODED[subKey];
  const out = {};
  keys.forEach((k) => {
    out[k] = resolveBool(
      has(pdSub, k) ? pdSub[k] : undefined,
      has(defSub, k) ? defSub[k] : undefined,
      hardSub[k]
    );
  });
  return out;
}

function trimmed(v) {
  return (v != null && String(v).trim()) ? String(v).trim() : null;
}

// Read one level's pricing mode for a view, falling back to that SAME level's
// legacy viewVisibility boolean. Returns null when the level says nothing.
//
// The per-level pairing is load-bearing. Checking every level's `pricing` first
// and only then falling back to legacy would let a practice default silently
// override a TC's explicit per-patient choice on an existing plan — the moment
// the practice set a house style, every plan built before this feature would
// flip. There is a test named for it.
function pricingModeAtLevel(level, view) {
  if (!level) return null;
  const mode = level.pricing && level.pricing[view];
  if (PRICING_MODES.indexOf(mode) !== -1) return mode;
  const legacy = level.viewVisibility && level.viewVisibility[view];
  if (typeof legacy === 'boolean') return legacy ? 'full' : 'none';
  return null;
}

function resolvePricingModes(pd, pdef) {
  const out = {};
  ['region', 'itemized'].forEach((view) => {
    out[view] = pricingModeAtLevel(pd, view)
      || pricingModeAtLevel(pdef, view)
      || HARDCODED.pricing[view];
  });
  return out;
}

/**
 * @param {object|null} pricingDisplay      per-patient blob (Annotations.pricingDisplay)
 * @param {object|null} practiceDefaults    settings displayDefaults blob
 * @returns {{pricing, viewVisibility, layers, itemizedGroupByPriority, itemizedGroupLabel, itemizedGroupSubtotals, showDiagnostic}}
 */
function resolvePricingDisplay(pricingDisplay, practiceDefaults) {
  const pd = pricingDisplay || {};
  const pdef = practiceDefaults || {};

  const pricing = resolvePricingModes(pd, pdef);

  return {
    pricing,
    // Still emitted, and still derived the old way, so anything that hasn't
    // moved to `pricing` yet (a cached patient bundle, an older PDF worker)
    // keeps resolving. `full` is the only mode that means "show per-row fees".
    viewVisibility: {
      region: pricing.region === 'full',
      itemized: pricing.itemized === 'full',
    },
    layers: resolveNested('layers', ['netFee', 'standardFee', 'insuranceEstimate', 'membership'], pd, pdef),
    itemizedGroupByPriority: resolveBool(
      has(pd, 'itemizedGroupByPriority') ? pd.itemizedGroupByPriority : undefined,
      has(pdef, 'itemizedGroupByPriority') ? pdef.itemizedGroupByPriority : undefined,
      HARDCODED.itemizedGroupByPriority
    ),
    itemizedGroupSubtotals: resolveBool(
      has(pd, 'itemizedGroupSubtotals') ? pd.itemizedGroupSubtotals : undefined,
      has(pdef, 'itemizedGroupSubtotals') ? pdef.itemizedGroupSubtotals : undefined,
      HARDCODED.itemizedGroupSubtotals
    ),
    showDiagnostic: resolveBool(
      has(pd, 'showDiagnostic') ? pd.showDiagnostic : undefined,
      has(pdef, 'showDiagnostic') ? pdef.showDiagnostic : undefined,
      HARDCODED.showDiagnostic
    ),
    showBalance: resolveBool(
      has(pd, 'showBalance') ? pd.showBalance : undefined,
      has(pdef, 'showBalance') ? pdef.showBalance : undefined,
      HARDCODED.showBalance
    ),
    showPhotos: resolveBool(
      has(pd, 'showPhotos') ? pd.showPhotos : undefined,
      has(pdef, 'showPhotos') ? pdef.showPhotos : undefined,
      HARDCODED.showPhotos
    ),
    itemizedGroupLabel:
      trimmed(pd.itemizedGroupLabel) || trimmed(pdef.itemizedGroupLabel) || HARDCODED.itemizedGroupLabel,
  };
}

/**
 * Build the pricingDisplay patch for setting one view's pricing mode.
 *
 * Writes the legacy viewVisibility boolean alongside the new mode ON PURPOSE.
 * Patient links are long-lived and public, so a browser holding a cached older
 * bundle reads only viewVisibility. Mirroring means 'total' and 'none' both
 * degrade to price-suppressed there: under-disclosing money is the safe failure
 * direction, and an old bundle showing full per-procedure pricing when the TC
 * chose total-only is the outcome we cannot have.
 */
function buildPricingModePatch(current, view, mode) {
  const safeMode = PRICING_MODES.indexOf(mode) !== -1 ? mode : 'full';
  const cur = current || {};
  return {
    pricing: { ...(cur.pricing || {}), [view]: safeMode },
    viewVisibility: { ...(cur.viewVisibility || {}), [view]: safeMode === 'full' },
  };
}

// ──────────────────────────── SHARED BODY END ────────────────────────────

export { resolvePricingDisplay, buildPricingModePatch, PRICING_MODES, HARDCODED };
